import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../../utils/axios';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  PlusIcon,
  PencilIcon,
  MapPinIcon,
  XMarkIcon,
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
  DocumentTextIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import CreateCompanyModal from '../../components/admin/CreateCompanyModal';
import { LoadingSpinner, EmptyState, AccessibleButton, FormInput, Modal } from '../../components/ui';
import { useDebounce } from '../../hooks/useDebounce';

const AgentCompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('assigned_at'); // Default: newest assigned
  const [sortOrder, setSortOrder] = useState('DESC'); // ASC or DESC
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [creditFormData, setCreditFormData] = useState({
    credit_limit: '',
    payment_terms: '',
    notes: ''
  });
  const [editFormData, setEditFormData] = useState({
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    website: '',
    notes: ''
  });
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Draft carts state - keyed by company_id
  const [draftCarts, setDraftCarts] = useState({});
  
  // Lazy loading state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCompanies, setTotalCompanies] = useState(0);

  // Intersection Observer for infinite scroll
  const observer = useRef();
  const lastCompanyElementRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  // Fetch draft carts for all companies
  const fetchDraftCarts = async () => {
    try {
      const response = await axios.get('/api/field-agent/draft-carts');
      const carts = response.data.draftCarts || [];
      
      // Create a map of company_id -> draft cart info
      const cartMap = {};
      carts.forEach(cart => {
        if (cart.company_id && cart.cart_data?.items?.length > 0) {
          cartMap[cart.company_id] = {
            itemCount: cart.cart_data.items.length,
            lastUpdated: cart.last_updated || cart.updated_at,
            totalQuantity: cart.cart_data.items.reduce((sum, item) => sum + (item.quantity || 1), 0)
          };
        }
      });
      
      setDraftCarts(cartMap);
    } catch (error) {
      console.error('Error fetching draft carts:', error);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchCompanies(1, true);
    fetchDraftCarts();
  }, []);

  // Reset and refetch when filters change
  useEffect(() => {
    setCompanies([]);
    setPage(1);
    setHasMore(true);
    fetchCompanies(1, true);
  }, [statusFilter, debouncedSearchTerm, sortBy, sortOrder]);

  // Load more when page changes
  useEffect(() => {
    if (page > 1) {
      fetchCompanies(page, false);
    }
  }, [page]);

  const fetchCompanies = async (pageNum = 1, isInitial = false) => {
    if (loadingMore && !isInitial) return;
    
    try {
      if (isInitial) {
      setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams({
        page: pageNum,
        limit: 50 // Load 50 companies at a time
      });

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }

      // Add sorting parameters
      if (sortBy) {
        params.append('sortBy', sortBy);
      }
      if (sortOrder) {
        params.append('sortOrder', sortOrder);
      }

      const response = await axios.get(`/api/field-agent/companies?${params}`);
      const fetchedCompanies = response.data.companies || [];
      
      // Append or replace companies
      setCompanies(prev => pageNum === 1 ? fetchedCompanies : [...prev, ...fetchedCompanies]);
      setTotalCompanies(response.data.total || 0);
      setHasMore(fetchedCompanies.length === 50); // If we got full page, there might be more
      
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('שגיאה בטעינת החברות');
      setHasMore(false);
    } finally {
      if (isInitial) {
      setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled via debouncedSearchTerm useEffect
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setEditFormData({
      phone: company.phone || '',
      address: company.address || '',
      city: company.city || '',
      postal_code: company.postal_code || '',
      website: company.website || '',
      notes: company.notes || ''
    });
    setEditErrors({});
    setShowEditModal(true);
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (editFormData.phone && !/^[\d\s\-\(\)]+$/.test(editFormData.phone)) {
      errors.phone = 'מספר טלפון לא תקין';
    }
    
    if (editFormData.website && !/^https?:\/\/.+/.test(editFormData.website)) {
      errors.website = 'כתובת אתר חייבת להתחיל ב-http:// או https://';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCompany = async () => {
    if (!validateEditForm()) {
      toast.error('נא לתקן את השגיאות בטופס');
      return;
    }

    try {
      setSaving(true);
      await axios.put(`/api/field-agent/companies/${editingCompany.id}`, editFormData);
      
      // Update local state
      setCompanies(prev => prev.map(item => {
        const company = item.company || item;
        if (company.id === editingCompany.id) {
          return {
            ...item,
            company: { ...company, ...editFormData }
          };
        }
        return item;
      }));
      
      toast.success('החברה עודכנה בהצלחה');
      closeEditModal();
    } catch (error) {
      console.error('Error updating company:', error);
      const errorMessage = error.response?.data?.message || 'שגיאה בעדכון החברה';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const closeEditModal = () => {
    if (saving) return; // Prevent closing while saving
    setShowEditModal(false);
    setEditingCompany(null);
    setEditFormData({
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      website: '',
      notes: ''
    });
    setEditErrors({});
  };

  const handleViewCredit = async (company) => {
    setEditingCompany(company);
    setLoadingCredit(true);
    setShowCreditModal(true);
    
    try {
      // Fetch company credit info
      const response = await axios.get(`/api/credit/company/${company.id}`);
      setCreditData(response.data.credit || null);
      
      // Set form data
      setCreditFormData({
        credit_limit: response.data.credit?.credit_limit || company.credit_limit || '',
        payment_terms: response.data.credit?.payment_terms || company.payment_terms || '30',
        notes: response.data.credit?.notes || ''
      });
    } catch (error) {
      console.error('Error fetching credit:', error);
      // If no credit found, use company defaults
      setCreditData(null);
      setCreditFormData({
        credit_limit: company.credit_limit || '',
        payment_terms: company.payment_terms || '30',
        notes: ''
      });
    } finally {
      setLoadingCredit(false);
    }
  };

  const handleSaveCredit = async () => {
    try {
      setSaving(true);
      
      await axios.put(`/api/field-agent/companies/${editingCompany.id}/credit`, {
        credit_limit: parseFloat(creditFormData.credit_limit) || 0,
        payment_terms: parseInt(creditFormData.payment_terms) || 30,
        notes: creditFormData.notes
      });
      
      toast.success('מסגרת האשראי עודכנה בהצלחה');
      setShowCreditModal(false);
      fetchCompanies(); // Refresh companies list
    } catch (error) {
      console.error('Error updating credit:', error);
      const errorMessage = error.response?.data?.message || 'שגיאה בעדכון מסגרת האשראי';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const closeCreditModal = () => {
    if (saving) return;
    setShowCreditModal(false);
    setEditingCompany(null);
    setCreditData(null);
    setCreditFormData({
      credit_limit: '',
      payment_terms: '',
      notes: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'אין תאריך';
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { text: 'פעיל', className: 'bg-green-100 text-green-800 border border-green-200' },
      inactive: { text: 'לא פעיל', className: 'bg-red-100 text-red-800 border border-red-200' },
      pending: { text: 'ממתין', className: 'bg-yellow-100 text-yellow-800 border border-yellow-200' }
    };

    const badge = statusMap[status] || { text: status, className: 'bg-gray-100 text-gray-800 border border-gray-200' };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header - Mobile First */}
      <div className="space-y-4">
        <div className="text-center sm:text-right">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">החברות שלי</h1>
          <p className="mt-2 text-sm text-gray-600">
            רשימת החברות המשויכות אליך
          </p>
        </div>
        
        {/* Create Company Button - Mobile Optimized */}
        <div className="flex justify-center sm:justify-end">
          <AccessibleButton
            onClick={() => setShowCreateModal(true)}
            variant="primary"
            size="lg"
            icon={PlusIcon}
            iconPosition="left"
            fullWidth
            className="sm:w-auto shadow-lg"
            ariaLabel="צור חברה חדשה"
          >
            צור חברה חדשה
          </AccessibleButton>
        </div>
      </div>

      {/* Filters - Mobile Optimized */}
      <div className="bg-white shadow-lg rounded-xl border border-gray-100 p-4 sm:p-6">
        <div className="space-y-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="w-full">
            <div className="relative">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="חיפוש לפי שם, מספר עוסק או אימייל..."
                className="block w-full pr-10 pl-10 py-2.5 text-sm border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent touch-manipulation transition-all"
                aria-label="חיפוש חברות"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute inset-y-0 left-0 pl-3 flex items-center touch-manipulation"
                  aria-label="נקה חיפוש"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                </button>
              )}
            </div>
            {debouncedSearchTerm && searchTerm !== debouncedSearchTerm && (
              <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1">
                <span className="animate-pulse">●</span>
                מחפש...
              </p>
            )}
          </form>

          {/* Filters Row - Improved Layout */}
          <div className="flex flex-col md:flex-row gap-3">
            {/* Status Filter */}
            <div className="flex flex-col gap-2 w-full md:w-48">
              <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">
              סטטוס:
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
              }}
                className="block w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent touch-manipulation bg-white transition-all"
              aria-label="סנן לפי סטטוס"
            >
              <option value="">כל הסטטוסים</option>
              <option value="active">פעיל</option>
              <option value="inactive">לא פעיל</option>
              <option value="pending">ממתין</option>
            </select>
            </div>

            {/* Sort By - Takes remaining space */}
            <div className="flex flex-col gap-2 flex-1">
              <label htmlFor="sort-by" className="text-sm font-medium text-gray-700">
                מיין לפי:
              </label>
              <div className="flex gap-2">
                <select
                  id="sort-by"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                  }}
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent touch-manipulation bg-white transition-all"
                  aria-label="בחר שדה למיון"
                >
                  <option value="assigned_at">החדש ביותר (תאריך הקצאה)</option>
                  <option value="name">שם החברה</option>
                  <option value="created_at">תאריך יצירה</option>
                  <option value="orders_count">מספר הזמנות</option>
                  <option value="last_order_date">תאריך הזמנה אחרונה</option>
                </select>
                <button
                  onClick={() => {
                    setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
                  }}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all touch-manipulation min-w-[44px] flex items-center justify-center"
                  aria-label={`מיון ${sortOrder === 'ASC' ? 'עולה' : 'יורד'}`}
                  title={sortOrder === 'ASC' ? 'מיון עולה' : 'מיון יורד'}
                >
                  {sortOrder === 'ASC' ? (
                    <ArrowUpIcon className="h-5 w-5 text-gray-600" aria-hidden="true" />
                  ) : (
                    <ArrowDownIcon className="h-5 w-5 text-gray-600" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Results info */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="font-medium">{totalCompanies}</span>
              <span>חברות {statusFilter && `(${statusFilter === 'active' ? 'פעילות' : statusFilter === 'inactive' ? 'לא פעילות' : 'ממתינות'})`}</span>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50">
                {sortOrder === 'ASC' ? (
                  <>
                    <BarsArrowUpIcon className="h-4 w-4 text-primary-600" aria-hidden="true" />
                    <span className="text-xs font-medium text-gray-700">עולה</span>
                  </>
                ) : (
                  <>
                    <BarsArrowDownIcon className="h-4 w-4 text-primary-600" aria-hidden="true" />
                    <span className="text-xs font-medium text-gray-700">יורד</span>
                  </>
                )}
              </div>
            </div>
        </div>
      </div>

      {/* Companies List - Mobile Card Layout */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" color="primary" text="טוען חברות..." />
        </div>
      ) : companies.length === 0 ? (
        <div className="bg-white shadow-lg rounded-xl border border-gray-100">
          <EmptyState
            type="search"
            icon={BuildingOfficeIcon}
            title={searchTerm || statusFilter ? 'לא נמצאו חברות' : 'אין חברות משויכות'}
            description={
              searchTerm || statusFilter
                ? 'לא נמצאו חברות התואמות את החיפוש או הפילטר שלך'
                : 'אין חברות משויכות אליך כרגע'
            }
            actionText={!searchTerm && !statusFilter ? 'צור חברה ראשונה' : undefined}
            onAction={!searchTerm && !statusFilter ? () => setShowCreateModal(true) : undefined}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((item, index) => {
            // Handle both nested structure (from field-agent API) and flat structure (from seller-create)
            const company = item.company || item;
            const stats = item.stats || { orders_count: 0, last_order: null };
            const assignment = item.assignment || { assigned_at: company.created_at };
            const isLastElement = index === companies.length - 1;
            
            // Ensure company has required properties
            if (!company || !company.id) {
              console.warn('Invalid company data:', item);
              return null;
            }
            
            return (
              <div 
                key={company.id} 
                ref={isLastElement ? lastCompanyElementRef : null}
                className="bg-white shadow-lg rounded-xl border border-gray-100 hover:shadow-xl hover:border-primary-300 transition-all duration-300 overflow-hidden group"
              >
                {/* Company Header */}
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={`/agent/companies/${company.id}`} 
                        className="block hover:opacity-80 transition-opacity"
                        aria-label={`פרטי חברה ${company.name}`}
                      >
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                            {company.name}
                          </h3>
                          {getStatusBadge(company.status)}
                        </div>
                        {company.business_number && (
                          <p className="text-sm text-gray-500 truncate">
                            ח.פ: {company.business_number}
                          </p>
                        )}
                      </Link>
                    </div>
                    <div className="flex-shrink-0 ml-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-primary-100 to-blue-100 rounded-lg flex items-center justify-center group-hover:from-primary-200 group-hover:to-blue-200 transition-colors">
                        <BuildingOfficeIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                      </div>
                    </div>
                  </div>

                  {/* Company Details */}
                  <div className="space-y-2 mb-4">
                    {company.email && (
                      <div className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors">
                        <EnvelopeIcon className="h-4 w-4 ml-2 text-gray-400 flex-shrink-0" aria-hidden="true" />
                        <a 
                          href={`mailto:${company.email}`}
                          className="truncate hover:text-primary-600 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {company.email}
                        </a>
                      </div>
                    )}
                    {company.phone && (
                      <div className="flex items-center text-sm text-gray-600 group-hover:text-gray-700 transition-colors">
                        <PhoneIcon className="h-4 w-4 ml-2 text-gray-400 flex-shrink-0" aria-hidden="true" />
                        <a 
                          href={`tel:${company.phone}`}
                          className="truncate hover:text-primary-600 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {company.phone}
                        </a>
                      </div>
                    )}
                    {company.address && (
                      <div className="flex items-start text-sm text-gray-600 group-hover:text-gray-700 transition-colors">
                        <MapPinIcon className="h-4 w-4 ml-2 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="truncate">{company.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 text-center border border-gray-200 hover:border-primary-200 transition-colors">
                      <p className="text-xs text-gray-600 font-medium mb-1">הזמנות</p>
                      <p className="text-xl font-bold text-gray-900">
                        {stats.orders_count || 0}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 text-center border border-gray-200 hover:border-primary-200 transition-colors">
                      <p className="text-xs text-gray-600 font-medium mb-1">הזמנה אחרונה</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {stats.last_order ? formatDate(stats.last_order.created_at) : 'אין'}
                      </p>
                    </div>
                  </div>

                  {/* Draft Cart Indicator */}
                  {draftCarts[company.id] && (
                    <div className="mb-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="bg-amber-100 p-1.5 rounded-full">
                            <ShoppingCartIcon className="h-4 w-4 text-amber-600" aria-hidden="true" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-amber-900">
                              יש טיוטת הזמנה
                            </p>
                            <p className="text-xs text-amber-700">
                              {draftCarts[company.id].totalQuantity} פריטים בעגלה
                            </p>
                          </div>
                        </div>
                        <Link
                          to={`/agent/create-order/${company.id}`}
                          className="px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors touch-manipulation"
                          aria-label={`המשך הזמנה עבור ${company.name}`}
                        >
                          המשך
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <AccessibleButton
                      onClick={() => handleEditCompany(company)}
                      variant="outline"
                      size="md"
                      icon={PencilIcon}
                      iconPosition="left"
                      fullWidth
                      ariaLabel={`ערוך פרטי חברה ${company.name}`}
                    >
                      ערוך
                    </AccessibleButton>
                    <Link
                      to={`/agent/create-order/${company.id}`}
                      className="flex-1"
                      aria-label={draftCarts[company.id] ? `המשך הזמנה עבור ${company.name}` : `צור הזמנה עבור ${company.name}`}
                    >
                      <div className={`inline-flex items-center justify-center w-full px-4 py-2.5 text-sm sm:text-base font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all touch-manipulation min-h-[44px] ${
                        draftCarts[company.id]
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 active:from-amber-700 active:to-orange-700'
                          : 'bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 active:from-primary-800 active:to-blue-800'
                      }`}>
                        {draftCarts[company.id] ? (
                          <>
                            <ShoppingCartIcon className="h-4 w-4 ml-1" aria-hidden="true" />
                            המשך ({draftCarts[company.id].totalQuantity})
                          </>
                        ) : (
                          <>
                            <PlusIcon className="h-4 w-4 ml-1" aria-hidden="true" />
                            הזמנה
                          </>
                        )}
                      </div>
                    </Link>
                    </div>
                    <AccessibleButton
                      onClick={() => handleViewCredit(company)}
                      variant="outline"
                      size="md"
                      icon={DocumentTextIcon}
                      iconPosition="left"
                      fullWidth
                      ariaLabel={`צפה במסגרת אשראי ${company.name}`}
                      className="border-purple-300 text-purple-600 hover:bg-purple-50"
                    >
                      מסגרת אשראי
                    </AccessibleButton>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-200 border-t-primary-600 mx-auto mb-3"></div>
            <p className="text-gray-600 font-medium">טוען חברות נוספות...</p>
          </div>
        </div>
      )}

      {/* End of companies indicator */}
      {!hasMore && companies.length > 0 && (
        <div className="text-center py-6">
          <p className="text-gray-500">
            הוצגו כל החברות ({companies.length} {totalCompanies > 0 && `מתוך ${totalCompanies}`} חברות)
          </p>
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateModal && (
        <CreateCompanyModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCompanyCreated={() => {
            setShowCreateModal(false);
            fetchCompanies();
          }}
        />
      )}

      {/* Edit Company Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={closeEditModal}
        title={`עריכת חברה: ${editingCompany?.name || ''}`}
        size="lg"
        preventClose={saving}
        ariaLabel="עריכת פרטי חברה"
      >
        {editingCompany && (
          <div className="space-y-6">
            <p className="text-sm text-gray-600">עדכן את פרטי החברה</p>
            
            <div className="space-y-4">
              <FormInput
                label="טלפון"
                type="tel"
                name="phone"
                value={editFormData.phone}
                onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="הכנס מספר טלפון"
                error={editErrors.phone}
                pattern="^[\d\s\-\(\)]+$"
                helpText="מספר טלפון יכול להכיל ספרות, מקפים וסוגריים"
              />
              
              <FormInput
                label="כתובת"
                type="text"
                name="address"
                value={editFormData.address}
                onChange={(e) => setEditFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="הכנס כתובת"
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput
                  label="עיר"
                  type="text"
                  name="city"
                  value={editFormData.city}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="הכנס עיר"
                />
                
                <FormInput
                  label="מיקוד"
                  type="text"
                  name="postal_code"
                  value={editFormData.postal_code}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                  placeholder="הכנס מיקוד"
                />
              </div>
              
              <FormInput
                label="אתר אינטרנט"
                type="url"
                name="website"
                value={editFormData.website}
                onChange={(e) => setEditFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://example.com"
                error={editErrors.website}
                helpText="חייב להתחיל ב-http:// או https://"
              />
              
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  הערות
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder="הכנס הערות נוספות"
                />
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <AccessibleButton
                onClick={closeEditModal}
                disabled={saving}
                variant="ghost"
                size="md"
                ariaLabel="ביטול עריכה"
              >
                ביטול
              </AccessibleButton>
              <AccessibleButton
                onClick={handleSaveCompany}
                disabled={saving}
                loading={saving}
                variant="primary"
                size="md"
                ariaLabel="שמור שינויים"
              >
                שמור
              </AccessibleButton>
            </div>
          </div>
        )}
      </Modal>

      {/* Credit Modal */}
      <Modal
        isOpen={showCreditModal}
        onClose={closeCreditModal}
        title={`מסגרת אשראי: ${editingCompany?.name || ''}`}
        size="lg"
        preventClose={saving}
        ariaLabel="צפייה ועריכת מסגרת אשראי"
      >
        {loadingCredit ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <LoadingSpinner size="lg" color="primary" text="טוען נתוני אשראי..." />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Credit Summary */}
            {creditData && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">מסגרת אשראי</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ₪{parseFloat(creditData.credit_limit || 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">אשראי בשימוש</p>
                    <p className="text-2xl font-bold text-red-600">
                      ₪{parseFloat(creditData.used_credit || 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">זמין לשימוש</p>
                    <p className="text-2xl font-bold text-green-600">
                      ₪{(parseFloat(creditData.credit_limit || 0) - parseFloat(creditData.used_credit || 0) - parseFloat(creditData.reserved_credit || 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <p className="text-sm text-gray-600">עדכן את מסגרת האשראי של החברה</p>
            
            <div className="space-y-4">
              <FormInput
                label="מסגרת אשראי (₪) *"
                type="number"
                name="credit_limit"
                value={creditFormData.credit_limit}
                onChange={(e) => setCreditFormData(prev => ({ ...prev, credit_limit: e.target.value }))}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
                autoFocus
                helpText="סכום מקסימלי שהחברה יכולה לחייב"
              />
              
              <FormInput
                label="תנאי תשלום (ימים)"
                type="number"
                name="payment_terms"
                value={creditFormData.payment_terms}
                onChange={(e) => setCreditFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                placeholder="30"
                min="0"
                required
                helpText="מספר ימים לתשלום"
              />
              
              <div>
                <label htmlFor="credit_notes" className="block text-sm font-medium text-gray-700 mb-2">
                  הערות
                </label>
                <textarea
                  id="credit_notes"
                  name="notes"
                  value={creditFormData.notes}
                  onChange={(e) => setCreditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder="הערות נוספות על מסגרת האשראי"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <AccessibleButton
                onClick={closeCreditModal}
                disabled={saving}
                variant="ghost"
                size="md"
                ariaLabel="ביטול"
              >
                ביטול
              </AccessibleButton>
              <AccessibleButton
                onClick={handleSaveCredit}
                disabled={saving || !creditFormData.credit_limit}
                loading={saving}
                variant="primary"
                size="md"
                ariaLabel="שמור שינויים"
              >
                שמור
              </AccessibleButton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AgentCompaniesPage;